import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ScmConnectionTestResult } from '@/api/schemas';
import { toast } from 'sonner';
import { useScmConnectionMutations } from '@/api/hooks';
import { hasErrors, type Attempt } from '@/features/integrations/credentialModel';
import { shownError } from '@/features/integrations/providerAccountModel';
import { submitConnect } from '@/features/integrations/submitConnect';
import { useSubmitState } from '@/features/integrations/useSubmitState';
import { connectScmConnection } from './connectScmConnection';
import { proposedName, type ScmTokenKind } from './tokenGuideModel';
import { useDiscoverScmToken } from './useDiscoverScmToken';
import { useVerifyScmToken } from './useVerifyScmToken';
import { SCM_CREDENTIALS_MISSING, initialScmValues, scmErrors, type ScmField, type ScmValues } from './scmConnectionModel';

/** Once Bitbucket answered for the repository, its own name goes in Name, unless the person typed one. */
function useProposedName(result: ScmConnectionTestResult | null, touched: boolean, setName: (name: string) => void) {
  const proposal = result ? proposedName(result) : undefined;
  useEffect(() => {
    if (proposal && !touched) setName(proposal);
  }, [proposal, touched]);
}

/** State and submit of the "Connect Bitbucket" form (a repository or a workspace token): what is typed, what is valid, the token stored by a failed attempt. */
export function useConnectScmForm(onDone: () => void) {
  const { create } = useScmConnectionMutations();
  const [values, setValues] = useState<ScmValues>(initialScmValues);
  /** The token stored by an earlier attempt whose connection failed (a taken workspace): the retry attaches it instead of storing it twice. */
  const [stored, setStored] = useState<string | null>(null);
  const attempt = useRef<Attempt | null>(null);
  const sub = useSubmitState();
  const local = scmErrors(values, Boolean(stored));
  const [nameTouched, setNameTouched] = useState(false);
  const set = (key: ScmField, value: string) => {
    if (key === 'name') setNameTouched(true);
    setValues((s) => ({ ...s, [key]: value }));
  };
  const setKind = (kind: ScmTokenKind) => setValues((s) => ({ ...s, kind }));
  const errorFor = (field: ScmField) => shownError(sub.server, local, field, sub.submitted);
  const source = useDiscoverScmToken(values, (s) => setValues((v) => ({ ...v, ...s })));
  const check = useVerifyScmToken(values);
  useProposedName(check.result, nameTouched, (name) => setValues((s) => ({ ...s, name })));

  const run = async () => {
    // A typed token is verified first (plan P34): one Bitbucket refuses, or that cannot read the source, is never stored;
    // the form stays open and shows the checks.
    if (!stored && !(check.result ?? (await check.verify())).ok) return false;
    const onStored = (credentialId: string) => {
      setStored(credentialId);
      set('token', '');
    };
    const connection = await connectScmConnection({ values, stored, attempt, create: (v) => create.mutateAsync(v), onStored });
    toast.success(`${connection.name} connected`, { description: 'Bitbucket accepted the token; Test connection checks it again at any time.' });
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    return submitConnect(sub, { invalid: hasErrors(local), run, onDone, slotField: values.kind === 'repository' ? 'repository' : 'workspace', missing: SCM_CREDENTIALS_MISSING });
  };
  return { values, set, setKind, source, check, stored, errorFor, onSubmit, busy: sub.busy || create.isPending || check.busy, problem: sub.problem, forgetToken: () => setStored(null) };
}

export type ConnectScmForm = ReturnType<typeof useConnectScmForm>;
